import _ from 'lodash';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {NumberInputBox} from '../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {
  buildCalendarEventVersionRemindersForValues,
  buildReminderValueForCalendarEventVersionReminder,
  CalendarReminderUnitsEnum,
  calendarReminderUnitToMinMaxValues,
  CalendarReminderValue,
} from '../../../helpers/calendarReminderHelpers';
import {CalendarEventVersionReminder} from '../../../models/api/calendarEventVersionModel';
import {CalendarReminderUnitDropdownBox} from './calendarReminderUnitDropdownBox';

/*
 * Props.
 */

export interface CalendarRemindersEditorProps {
  reminders: ReadonlyArray<CalendarEventVersionReminder> | undefined;
  onRemindersChange: HandlerOf<ReadonlyArray<CalendarEventVersionReminder>>;
}

/*
 * Styles.
 */

const StyledReminderWrapperDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 50px auto 1fr;
  grid-column-gap: 8px;
  margin-bottom: 8px;
  align-items: center;

  div {
    padding: 0px;
  }
`;

const StyledInputBoxWrapperDiv = styled.div`
  div {
    margin: 0px;
  }
`;

const StyledCalendarReminderUnitDropdownBox = styled(CalendarReminderUnitDropdownBox)`
  width: 100px;
`;

const StyledAddReminderButtonWrapperDiv = styled.div`
  display: flex;
  margin-left: -14px;
`;

const StyledRemoveButtonWrapperDiv = styled.div`
  display: flex;
`;

const StyledIconButton = styled(IconButton)`
  padding: 0px;
  &:hover {
    background: none;
  }
`;

/*
 * Component.
 */

export const CalendarRemindersEditor: FC<CalendarRemindersEditorProps> = (props) => {
  const {onRemindersChange} = props;
  const [reminderValues, setReminderValues] = useState<ReadonlyArray<CalendarReminderValue>>([]);

  // Set the initial reminders state.
  useInitialEffect(() => {
    const initialReminderValues = _(props.reminders || [])
      .sortBy((r) => r.minutesBefore)
      .map(buildReminderValueForCalendarEventVersionReminder)
      .value();

    setReminderValues(initialReminderValues);
  });

  // Update the parent when our reminders state changes.
  useEffect(() => {
    onRemindersChange(buildCalendarEventVersionRemindersForValues(reminderValues));
  }, [onRemindersChange, reminderValues]);

  const onReminderChange = (newReminderValue: CalendarReminderValue, index: number) => {
    setReminderValues((currentValues) => currentValues.map((v, i) => (i === index ? newReminderValue : v)));
  };

  const onReminderRemove = (index: number) => {
    setReminderValues((currentValues) => currentValues.filter((v, i) => i !== index));
  };

  const onAddReminderClick = () => {
    setReminderValues((currentValues) => [
      ...currentValues,
      {unit: CalendarReminderUnitsEnum.MINUTE, count: 10},
    ]);
  };

  return (
    <div>
      {reminderValues.map((r, index) =>
        renderReminder(
          r,
          index,
          (newReminderValue) => onReminderChange(newReminderValue, index),
          onReminderRemove,
        ),
      )}
      {reminderValues.length < 5 && (
        <StyledAddReminderButtonWrapperDiv>
          <Button variant={ButtonVariantsEnum.LINK} onClick={onAddReminderClick}>
            <Trans id="calendar-reminder-add-reminder">+ Add notification</Trans>
          </Button>
        </StyledAddReminderButtonWrapperDiv>
      )}
    </div>
  );
};

function renderReminder(
  r: CalendarReminderValue,
  index: number,
  onChange: HandlerOf<CalendarReminderValue>,
  onRemove: HandlerOf<number>,
) {
  return (
    <StyledReminderWrapperDiv key={`calendar-reminder-${index}`}>
      <StyledInputBoxWrapperDiv>
        <NumberInputBox
          value={r.count}
          onChange={(count) => {
            onChange({
              ...r,
              count: count || 0,
            });
          }}
          max={calendarReminderUnitToMinMaxValues[r.unit].max}
          min={calendarReminderUnitToMinMaxValues[r.unit].min}
        />
      </StyledInputBoxWrapperDiv>
      <StyledCalendarReminderUnitDropdownBox
        unit={r.unit}
        onChange={(unit) => {
          onChange({
            ...r,
            unit,
          });
        }}
      />
      <StyledRemoveButtonWrapperDiv>
        <StyledIconButton
          iconName="clearSmall"
          onClick={() => onRemove(index)}
          label={<Trans id="calendar-reminder-remove">Remove</Trans>}
        />
      </StyledRemoveButtonWrapperDiv>
    </StyledReminderWrapperDiv>
  );
}
